<template>
  <main class="app-shell" :class="{ 'is-mobile-layout': isMobileLayout }" @keydown.esc="closeMenu">
    <header class="topbar">
      <div class="topbar-status">
        <input
          class="client-id client-id-field"
          :value="clientId"
          type="text"
          aria-label="Identifiant client"
          title="Modifier l'identifiant client"
          @change="updateClientId"
        >
        <div class="media-controls" aria-label="Contrôles multimédias">
          <span
            class="volume-bar"
            :style="{ '--volume-level': `${volume}%` }"
            :aria-label="`Volume à ${volume}%`"
            role="img"
          ></span>
          <nav
            v-if="activeSublistVideos.length > 1"
            class="video-navigation"
            aria-label="Navigation dans la sous-liste vidéo"
          >
            <button
              class="previous-button"
              type="button"
              :disabled="activeSublistIndex <= 0"
              aria-label="Lire la vidéo précédente"
              @click="selectAdjacentVideo(-1)"
            >&lt;&lt;</button>
            <button class="brand-mark" type="button" aria-label="Accueil - mettre à jour l’application" @click="refreshPwa">
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M3 10.8 12 3l9 7.8v9.2a1 1 0 0 1-1 1h-5.2v-6.2H9.2V21H4a1 1 0 0 1-1-1v-9.2Z" />
              </svg>
            </button>
            <button
              class="next-button"
              type="button"
              :disabled="activeSublistIndex >= activeSublistVideos.length - 1"
              aria-label="Lire la vidéo suivante"
              @click="selectAdjacentVideo(1)"
            >&gt;&gt;</button>
          </nav>
          <div class="media-actions">
            <div class="playback-controls" aria-label="Contrôles de lecture">
              <button type="button" aria-label="Lire la vidéo" @click="sendPlaybackCommand('play')">▶</button>
              <button type="button" aria-label="Mettre la vidéo en pause" @click="sendPlaybackCommand('pause')">Ⅱ</button>
            </div>
            <div class="volume-controls" aria-label="Contrôles du volume">
              <button type="button" aria-label="Baisser le volume" @click="changeVolume(-10)">−</button>
              <button type="button" aria-label="Augmenter le volume" @click="changeVolume(10)">+</button>
            </div>
          </div>
        </div>
        <button
          class="sse-toggle"
          type="button"
          :aria-pressed="isSseEnabled"
          @click="toggleSse"
        >
          {{ isSseEnabled ? 'Couper SSE' : 'Activer SSE' }}
        </button>
      </div>
      <button
        class="menu-toggle"
        type="button"
        :aria-expanded="isMenuOpen"
        aria-controls="video-menu"
        :aria-label="isMenuOpen ? 'Fermer le menu des vidéos' : 'Ouvrir le menu des vidéos'"
        @click="toggleMenu"
      >
        <span></span>
        <span></span>
        <span></span>
      </button>
    </header>

    <transition name="notification">
      <div v-if="notification" class="notification-bubble" role="status" aria-label="Message reçu"></div>
    </transition>

    <transition name="fade">
      <button
        v-if="isMenuOpen"
        class="menu-backdrop"
        type="button"
        aria-label="Fermer le menu"
        @click="closeMenu"
      ></button>
    </transition>

    <section class="layout">
      <section class="panel player-panel" aria-label="Lecteur vidéo intégré">
        <div class="player-header">
          <div>
            <p class="player-kicker">Lecture embarquée</p>
            <h2>{{ activeVideo.title }}</h2>
          </div>

          <a
            :href="activeVideo.url"
            target="_blank"
            rel="noreferrer"
            @click="sendVideo(activeVideo.url)"
          >Ouvrir sur YouTube</a>
        </div>

        <div class="player-frame">
          <iframe
            :key="`${activeVideo.embedUrl}-${playerVersion}`"
            ref="youtubePlayer"
            :src="activeVideo.embedUrl"
            :title="activeVideo.title"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowfullscreen
            @load="flushPlaybackCommand"
          ></iframe>
        </div>
      </section>
    </section>

    <transition name="slide">
      <aside v-if="isMenuOpen || isMobileLayout" id="video-menu" class="video-menu panel" aria-label="Liens des vidéos">
        <div class="menu-heading">
          <div>
            <p class="eyebrow">À regarder</p>
            <h2>Liens vidéos</h2>
          </div>
          <button class="menu-close" type="button" aria-label="Fermer le menu" @click="closeMenu">×</button>
        </div>

        <p class="menu-count">{{ videos.length }} vidéos disponibles</p>

        <a
          v-for="video in visibleVideos"
          :key="video.id"
          class="video-card"
          :class="{ active: video.id === activeVideo.id }"
          :href="video.url"
          :aria-label="`Ouvrir ${video.title}`"
          @click.prevent="selectVideoAndExpand(video)"
        >
          <span class="video-index">{{ videoNumber(video) }}</span>
          <span class="video-thumbnail">
            <img
              :src="video.thumbnailUrl"
              :alt="`Miniature de ${video.title}`"
              @error="handleThumbnailError(video, $event)"
            >
            <span class="video-play" aria-hidden="true">▶</span>
          </span>
          <span class="video-meta">
            <span class="video-title">{{ video.title }}</span>
            <span class="video-url">{{ video.sourceLabel }}</span>
          </span>
          <span class="video-action">Ouvrir</span>
        </a>

        <button class="cache-refresh" type="button" @click="refreshPwa">
          Mettre à jour l’application
        </button>
      </aside>
    </transition>

    <section class="bottom-settings" aria-label="Connexion et formulaires">
      <button
        class="status-pill"
        :class="connectionStatusClass"
        type="button"
        :aria-expanded="showConnectionPanel"
        aria-controls="connection-panel"
        @click="toggleConnectionPanel"
      >{{ connectionLabel }}</button>
      <div v-if="showConnectionPanel" id="connection-panel" class="connection-panel">
        <div class="group-settings">
          <input
            id="group-id-field"
            class="client-id"
            v-model="groupIdDraft"
            type="text"
            aria-label="Identifiant du groupe"
            title="Modifier l’identifiant du groupe"
          >
          <button
            class="group-id-save"
            type="button"
            @click="saveGroupId"
          >Save</button>
        </div>
        <form class="video-send-form" @submit.prevent="sendVideoUrl">
          <label for="video-url-field">Partager une vidéo</label>
          <div class="video-send-fields">
            <input
              id="video-url-field"
              v-model="videoUrlDraft"
              type="url"
              inputmode="url"
              placeholder="https://…"
              autocomplete="url"
              required
              aria-describedby="video-send-feedback"
            >
            <button type="submit" :disabled="isSendingVideo">
              {{ isSendingVideo ? 'Envoi…' : 'Envoyer' }}
            </button>
          </div>
          <p v-if="sendFeedback" id="video-send-feedback" class="video-send-feedback" :class="`is-${sendFeedback.type}`" role="status">
            {{ sendFeedback.message }}
          </p>
        </form>
        <form class="video-send-form playlist-form" @submit.prevent="downloadPlaylist">
          <label for="playlist-field">Charger une playlist</label>
          <div class="video-send-fields">
            <input
              id="playlist-field"
              v-model="playlistDraft"
              type="text"
              placeholder="ID ou URL de playlist YouTube"
              autocomplete="off"
              required
              aria-describedby="playlist-feedback"
            >
            <button type="submit" :disabled="isDownloadingPlaylist">
              {{ isDownloadingPlaylist ? 'Chargement…' : 'Charger' }}
            </button>
          </div>
          <p v-if="playlistFeedback" id="playlist-feedback" class="video-send-feedback" :class="`is-${playlistFeedback.type}`" role="status">
            {{ playlistFeedback.message }}
          </p>
        </form>
      </div>
    </section>
  </main>
</template>

<script>
const videos = [
  {
    title: 'Exemple de vidéo YouTube',
    url: 'https://www.youtube.com/watch?v=ubYF_ZiFbV0&list=PLEQIzZbgsWEPGUq65t4M4GGhbY9EdncLx',
  },
  {
    title: 'Deuxième vidéo',
    url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
  },
  {
    title: 'Lien court YouTube',
    url: 'https://youtu.be/ubYF_ZiFbV0',
  },
]

function getYouTubeVideoId(url) {
  try {
    const parsedUrl = new URL(url)
    const hostname = parsedUrl.hostname.replace(/^www\./, '')

    if (hostname === 'youtu.be') {
      return parsedUrl.pathname.split('/').filter(Boolean)[0] || ''
    }

    if (hostname.endsWith('youtube.com')) {
      return parsedUrl.searchParams.get('v') || ''
    }
  } catch (error) {
    return ''
  }

  return ''
}

function getYouTubeThumbnail(url) {
  const videoId = getYouTubeVideoId(url)

  return videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : ''
}

function getYouTubeFallbackThumbnail(url) {
  const videoId = getYouTubeVideoId(url)

  return videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : ''
}

function normalizeVideoItems(items, listKey = 'default') {
  return items
    .map((item, index) => {
      const snippet = item?.snippet || {}
      const videoId = snippet.resourceId?.videoId

      if (!videoId) {
        return null
      }

      const url = `https://www.youtube.com/watch?v=${encodeURIComponent(videoId)}`
      const thumbnails = snippet.thumbnails || {}
      const thumbnailUrl = thumbnails.maxres?.url || thumbnails.standard?.url || thumbnails.high?.url || getYouTubeThumbnail(url)

      return {
        title: snippet.title || `Vidéo ${index + 1}`,
        url,
        id: videoId,
        listKey,
        embedUrl: toEmbedUrl(url),
        thumbnailUrl,
        fallbackThumbnailUrl: thumbnails.high?.url || getYouTubeFallbackThumbnail(url),
        sourceLabel: url,
      }
    })
    .filter(Boolean)
}

function toEmbedUrl(url) {
  try {
    const parsedUrl = new URL(url)
    const videoId = getYouTubeVideoId(url)

    if (!videoId) {
      return url
    }

    const embedUrl = new URL(`https://www.youtube-nocookie.com/embed/${videoId}`)
    const listId = parsedUrl.searchParams.get('list')

    if (listId) {
      embedUrl.searchParams.set('list', listId)
    }

    embedUrl.searchParams.set('enablejsapi', '1')
    embedUrl.searchParams.set('rel', '0')
    embedUrl.searchParams.set('autoplay', '1')
    return embedUrl.toString()
  } catch (error) {
    return url
  }
}
function generateClientId() {
  return Math.random().toString(36).slice(2, 6) + Math.floor(Math.random() * 10)
}

function safeParseMessage(data) {
  if (typeof data !== 'string') {
    return data
  }

  try {
    return JSON.parse(data)
  } catch (error) {
    return data
  }
}

function findYouTubeUrl(value) {
  if (typeof value === 'string') {
    const match = value.match(/https?:\/\/(?:www\.)?(?:youtube\.com|youtu\.be)\/[^\s"'<>]+/i)
    return match ? match[0].replace(/[),.;]+$/, '') : ''
  }

  if (value && typeof value === 'object') {
    for (const nestedValue of Object.values(value)) {
      const url = findYouTubeUrl(nestedValue)

      if (url) {
        return url
      }
    }
  }

  return ''
}

export default {
  name: 'App',
  data() {
    return {
      videos: videos.map((video) => ({
        ...video,
        id: getYouTubeVideoId(video.url) || video.url,
        embedUrl: toEmbedUrl(video.url),
        thumbnailUrl: getYouTubeThumbnail(video.url),
        fallbackThumbnailUrl: getYouTubeFallbackThumbnail(video.url),
        sourceLabel: video.url,
      })),
      activeVideo: null,
      defaultVideoIds: new Set(),
      expandedLists: new Set(),
      playerVersion: 0,
      isMenuOpen: false,
      isMobileLayout: false,
      showConnectionPanel: false,
      clientId: '',
      groupId: '',
      groupIdDraft: '',
      videoUrlDraft: '',
      isSendingVideo: false,
      sendFeedback: null,
      playlistDraft: '',
      isDownloadingPlaylist: false,
      playlistFeedback: null,
      isSseEnabled: true,
      connectionStatus: 'connecting',
      notification: null,
      notificationTimer: null,
      eventSource: null,
      reconnectTimer: null,
      reconnectAttempts: 0,
      reconnectDelayMs: 1000,
      pendingPlaybackCommand: null,
      volume: 100,
    }
  },
  computed: {
    shortClientId() {
      return this.clientId ? `${this.clientId.slice(0, 8)}…` : '—'
    },
    connectionLabel() {
      if (this.connectionStatus === 'connected') {
        return 'Connecté'
      }

      if (this.connectionStatus === 'disconnected') {
        return 'Déconnecté'
      }

      return 'Connexion SSE'
    },
    connectionStatusClass() {
      return `is-${this.connectionStatus}`
    },
    visibleVideos() {
      if (this.defaultVideoIds.size === 0) {
        return this.videos.slice(0, 2)
      }

      return this.videos.filter((video) =>
        this.defaultVideoIds.has(video.id) || this.expandedLists.has(video.listKey)
      )
    },
    activeSublistVideos() {
      if (!this.activeVideo) {
        return []
      }

      return this.videos.filter((video) => video.listKey === this.activeVideo.listKey)
    },
    activeSublistIndex() {
      return this.activeSublistVideos.findIndex((video) => video.id === this.activeVideo?.id)
    },
  },
  created() {
    this.activeVideo = this.videos[0]
    this.clientId = this.createClientId()
    this.groupId = this.getOrCreateGroupId()
    this.groupIdDraft = this.groupId
    this.loadVideos()
    this.connectSse()
  },
  mounted() {
    window.addEventListener('online', this.handleOnline)
    window.addEventListener('offline', this.handleOffline)
    window.addEventListener('resize', this.updateMobileLayout)
    document.addEventListener('click', this.handleVibrationClick)
    this.updateMobileLayout()
  },
  beforeUnmount() {
    window.removeEventListener('online', this.handleOnline)
    window.removeEventListener('offline', this.handleOffline)
    window.removeEventListener('resize', this.updateMobileLayout)
    document.removeEventListener('click', this.handleVibrationClick)
    this.disconnectSse()
  },
  methods: {
    selectAdjacentVideo(direction) {
      const nextIndex = this.activeSublistIndex + direction
      const nextVideo = this.activeSublistVideos[nextIndex]

      if (nextVideo) {
        this.selectVideoAndExpand(nextVideo)
      }
    },
    videoNumber(video) {
      const isMainVideo = this.defaultVideoIds.has(video.id)
      const numberedVideos = isMainVideo
        ? this.visibleVideos.filter((item) => this.defaultVideoIds.has(item.id))
        : this.visibleVideos.filter((item) => item.listKey === video.listKey)
      const index = numberedVideos.findIndex((item) => item.id === video.id)

      return String(index + 1).padStart(2, '0')
    },
    async loadVideos() {
      try {
        const response = await fetch(`/data?group_id=${encodeURIComponent(this.groupId)}`)
        if (!response.ok) {
          throw new Error(`Video data request failed: ${response.status}`)
        }

        const data = await response.json()
        const defaultItems = Array.isArray(data.lists)
          ? data.lists.map((list) => ({ item: list.items?.[0], listKey: list.source })).filter(({ item }) => item)
          : data.items || []
        const loadedVideos = Array.isArray(data.lists)
          ? defaultItems.flatMap(({ item, listKey }) => normalizeVideoItems([item], listKey))
          : normalizeVideoItems(defaultItems)
        const allVideos = Array.isArray(data.lists)
          ? data.lists.flatMap((list) => normalizeVideoItems(list.items || [], list.source))
          : normalizeVideoItems(data.items || defaultItems)

        if (loadedVideos.length > 0) {
          this.videos = allVideos.length > 0 ? allVideos : loadedVideos
          this.defaultVideoIds = new Set(loadedVideos.map((video) => video.id))
          this.activeVideo = loadedVideos[0]
          this.playerVersion += 1
        }
      } catch (error) {
        // Keep the local fallback list if the data endpoint is unavailable.
      }
    },
    createClientId() {
      try {
        // Remove the legacy persisted value; client IDs are session-only.
        window.localStorage.removeItem('tv-client-id')
      } catch (error) {
        // Continue with a generated ID when storage is unavailable.
      }

      return generateClientId()
    },
    getOrCreateGroupId() {
      const storageKey = 'tv-group-id'

      try {
        const existing = window.localStorage.getItem(storageKey)
        if (existing) return existing

        const defaultGroup = 'default'
        return defaultGroup
      } catch (error) {
        return 'default'
      }
    },
    updateClientId(event) {
      const nextClientId = event.target.value.trim()

      if (!nextClientId) {
        event.target.value = this.clientId
        return
      }

      if (nextClientId === this.clientId) {
        return
      }

      this.clientId = nextClientId
      this.connectSse()
    },
    saveGroupId() {
      const nextGroupId = this.groupIdDraft.trim()

      if (!nextGroupId) {
        this.groupIdDraft = this.groupId
        return
      }

      if (nextGroupId === this.groupId) return

      this.groupId = nextGroupId
      this.groupIdDraft = nextGroupId
      try {
        window.localStorage.setItem('tv-group-id', nextGroupId)
      } catch (error) {
        // Continue with the editable group ID for this session.
      }
      this.connectSse()
      this.loadVideos()
    },
    toggleSse() {
      this.isSseEnabled = !this.isSseEnabled

      if (this.isSseEnabled) {
        this.connectSse()
      } else {
        this.disconnectSse()
        this.connectionStatus = 'disconnected'
      }
    },
    connectSse() {
      if (!this.isSseEnabled || !this.clientId || !this.groupId || typeof window === 'undefined') {
        return
      }

      this.disconnectSse()
      this.connectionStatus = 'connecting'

      const endpoint = `${process.env.VUE_APP_SSE_PATH || '/events'}?client_id=${encodeURIComponent(this.clientId)}&group_id=${encodeURIComponent(this.groupId)}`
      const eventSource = new EventSource(endpoint)
      this.eventSource = eventSource

      eventSource.addEventListener('heartbeat', (event) => {
        if (this.eventSource !== eventSource) return
        const heartbeat = safeParseMessage(event.data)
        fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            group_id: this.groupId,
            client_id: this.clientId,
            connection_id: heartbeat.connection_id,
          }),
        }).catch(() => {})
      })

      eventSource.onopen = () => {
        this.connectionStatus = 'connected'
        this.reconnectAttempts = 0
        this.reconnectDelayMs = 1000
      }

      eventSource.addEventListener('message', (event) => {
        const payload = safeParseMessage(event.data)
        this.showVideoFromPayload(payload)
        this.showNotification()
      })

      eventSource.onerror = () => {
        this.connectionStatus = 'disconnected'
        if (this.eventSource === eventSource) {
          eventSource.close()
          this.eventSource = null
        }
        this.scheduleReconnect()
      }
    },
    disconnectSse() {
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer)
        this.reconnectTimer = null
      }

      if (this.eventSource) {
        this.eventSource.close()
        this.eventSource = null
      }
    },
    scheduleReconnect() {
      if (!this.isSseEnabled || this.reconnectTimer || !navigator.onLine) {
        return
      }

      const delay = Math.min(this.reconnectDelayMs * Math.max(this.reconnectAttempts + 1, 1), 30000)
      this.reconnectAttempts += 1

      this.reconnectTimer = window.setTimeout(() => {
        this.reconnectTimer = null
        this.connectSse()
      }, delay)
    },
    handleOnline() {
      if (this.isSseEnabled && this.connectionStatus !== 'connected') {
        this.connectSse()
      }
    },
    handleOffline() {
      this.connectionStatus = 'disconnected'
    },
    showVideoFromPayload(payload) {
      const message = payload?.payload && typeof payload.payload === 'object' ? payload.payload : payload
      const command = message?.command
      const url = findYouTubeUrl(message)

      if (!url || !getYouTubeVideoId(url)) {
        return
      }

      const videoId = getYouTubeVideoId(url)
      const previousVideoId = getYouTubeVideoId(this.activeVideo?.url)
      const existingVideo = this.videos.find((video) => getYouTubeVideoId(video.url) === videoId)

      this.activeVideo = existingVideo
        ? { ...existingVideo, embedUrl: toEmbedUrl(url) }
        : {
            title: 'Vidéo reçue',
            url,
            id: videoId,
            embedUrl: toEmbedUrl(url),
            thumbnailUrl: getYouTubeThumbnail(url),
            fallbackThumbnailUrl: getYouTubeFallbackThumbnail(url),
            sourceLabel: url,
          }
      if (command === 'play' || command === 'pause' || command === 'volume') {
        const nextVolume = Number(message?.value)
        this.pendingPlaybackCommand = command === 'volume'
          ? { command, value: Math.max(0, Math.min(100, Number.isFinite(nextVolume) ? nextVolume : this.volume)) }
          : { command }
        if (command === 'volume') {
          this.volume = this.pendingPlaybackCommand.value
        }
        if (previousVideoId !== videoId) {
          this.playerVersion += 1
        }
        this.$nextTick(() => this.flushPlaybackCommand())
        return
      }

      this.playerVersion += 1
    },
    sendPlaybackCommand(command) {
      if (!this.activeVideo?.url || !this.clientId || !this.groupId) {
        return
      }

      this.pendingPlaybackCommand = { command }
      this.$nextTick(() => this.flushPlaybackCommand())

      fetch('/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          group_id: this.groupId,
          sender: this.clientId,
          payload: {
            command,
            url: this.activeVideo.url,
          },
        }),
      }).catch(() => {
        // The local player remains controllable if message delivery fails.
      })
    },
    changeVolume(delta) {
      if (!this.activeVideo?.url || !this.clientId || !this.groupId) {
        return
      }

      this.volume = Math.max(0, Math.min(100, this.volume + delta))
      this.pendingPlaybackCommand = { command: 'volume', value: this.volume }
      this.$nextTick(() => this.flushPlaybackCommand())

      fetch('/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          group_id: this.groupId,
          sender: this.clientId,
          payload: {
            command: 'volume',
            value: this.volume,
            url: this.activeVideo.url,
          },
        }),
      }).catch(() => {
        // Keep the local volume if message delivery fails.
      })
    },
    flushPlaybackCommand() {
      const instruction = this.pendingPlaybackCommand
      const player = this.$refs.youtubePlayer

      if (!instruction || !player?.contentWindow) {
        return
      }

      const command = typeof instruction === 'string' ? instruction : instruction.command
      player.contentWindow.postMessage(JSON.stringify({
        event: 'command',
        func: command === 'play'
          ? 'playVideo'
          : command === 'pause'
            ? 'pauseVideo'
            : 'setVolume',
        args: command === 'volume' ? [instruction.value] : [],
      }), '*')
      this.pendingPlaybackCommand = null
    },
    showNotification() {
      this.notification = true

      if (this.notificationTimer) {
        clearTimeout(this.notificationTimer)
      }

      this.notificationTimer = window.setTimeout(() => {
        this.notification = null
        this.notificationTimer = null
      }, 1000)
    },
    selectVideo(video) {
      this.activeVideo = video
      this.playerVersion += 1
      this.closeMenu()
      this.sendVideo(video.url)
    },
    async sendVideo(url) {
      if (!url || !this.clientId) {
        return
      }

      try {
        await fetch('/send', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            group_id: this.groupId,
            sender: this.clientId,
            payload: url,
          }),
        })
      } catch (error) {
        // The link should still open if message delivery fails.
      }
    },
    handleThumbnailError(video, event) {
      if (!video.fallbackThumbnailUrl || event.target.dataset.fallbackApplied === 'true') {
        return
      }

      event.target.dataset.fallbackApplied = 'true'
      event.target.src = video.fallbackThumbnailUrl
    },
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen
    },
    selectVideoAndExpand(video) {
      this.expandedLists = new Set([...this.expandedLists, video.listKey])
      this.selectVideo(video)
    },
    toggleConnectionPanel() {
      this.showConnectionPanel = !this.showConnectionPanel
      if (!this.showConnectionPanel) {
        this.sendFeedback = null
        this.playlistFeedback = null
      }
    },
    async sendVideoUrl() {
      const url = this.videoUrlDraft.trim()

      if (!url || !this.clientId || !this.groupId) {
        return
      }

      try {
        const parsedUrl = new URL(url)
        if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
          throw new Error('URL non prise en charge')
        }
      } catch (error) {
        this.sendFeedback = { type: 'error', message: 'Saisissez une URL vidéo valide.' }
        return
      }

      this.isSendingVideo = true
      this.sendFeedback = null

      try {
        const response = await fetch('/send', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            group_id: this.groupId,
            sender: this.clientId,
            payload: [url],
          }),
        })

        if (!response.ok) {
          throw new Error(`Send request failed: ${response.status}`)
        }

        this.videoUrlDraft = ''
        this.sendFeedback = { type: 'success', message: 'Vidéo envoyée au groupe.' }
      } catch (error) {
        this.sendFeedback = { type: 'error', message: 'Impossible d’envoyer la vidéo.' }
      } finally {
        this.isSendingVideo = false
      }
    },
    async downloadPlaylist() {
      const value = this.playlistDraft.trim()
      let playlistId = value

      try {
        const parsedUrl = new URL(value)
        playlistId = parsedUrl.searchParams.get('list') || ''
      } catch (error) {
        // The form also accepts a raw playlist ID.
      }

      if (!/^[A-Za-z0-9_-]+$/.test(playlistId) || !this.groupId) {
        this.playlistFeedback = { type: 'error', message: 'Saisissez un identifiant de playlist valide.' }
        return
      }

      this.isDownloadingPlaylist = true
      this.playlistFeedback = null

      try {
        const endpoint = `/playlist?group_id=${encodeURIComponent(this.groupId)}&list=${encodeURIComponent(playlistId)}`
        const response = await fetch(endpoint)
        const data = await response.json().catch(() => ({}))

        if (!response.ok) {
          throw new Error(data.error || 'Playlist indisponible')
        }

        await this.loadVideos()
        this.playlistDraft = ''
        this.playlistFeedback = {
          type: 'success',
          message: `${data.items || 0} vidéo(s) ajoutée(s) au groupe.`,
        }
      } catch (error) {
        this.playlistFeedback = { type: 'error', message: error.message || 'Impossible de charger la playlist.' }
      } finally {
        this.isDownloadingPlaylist = false
      }
    },
    updateMobileLayout() {
      this.isMobileLayout = window.innerWidth <= 920 || window.matchMedia('(pointer: coarse)').matches
    },
    handleVibrationClick(event) {
      const button = event.target.closest('button, .vibrant')

      if (button && 'vibrate' in navigator) {
        navigator.vibrate(100)
      }
    },
    async refreshPwa() {
      if ('caches' in window) {
        const cacheKeys = await window.caches.keys()
        await Promise.all(cacheKeys.map((cacheKey) => window.caches.delete(cacheKey)))
      }

      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations()
        await Promise.all(registrations.map((registration) => registration.unregister()))
      }

      window.location.reload()
    },
    closeMenu() {
      this.isMenuOpen = false
    },
  },
}
</script>

<style>
:root {
  color-scheme: light;
  --bg: #0f172a;
  --bg-soft: rgba(15, 23, 42, 0.78);
  --surface: rgba(255, 255, 255, 0.08);
  --surface-strong: rgba(255, 255, 255, 0.14);
  --text: #e2e8f0;
  --muted: #94a3b8;
  --accent: #38bdf8;
  --accent-strong: #0ea5e9;
  --border: rgba(148, 163, 184, 0.2);
  --success: #34d399;
  --warning: #fbbf24;
}

* {
  box-sizing: border-box;
  -webkit-user-select: none;
  user-select: none;
}

html,
body,
#app {
  touch-action: manipulation;
}

body {
  margin: 0;
  min-height: 100vh;
  -webkit-touch-callout: none;
  font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  background:
    radial-gradient(circle at top left, rgba(56, 189, 248, 0.25), transparent 30%),
    radial-gradient(circle at bottom right, rgba(14, 165, 233, 0.22), transparent 28%),
    linear-gradient(180deg, #020617 0%, #0f172a 50%, #111827 100%);
  color: var(--text);
}

button,
a {
  font: inherit;
}

#app {
  min-height: 100vh;
}

.app-shell {
  width: min(1180px, calc(100vw - 32px));
  margin: 0 auto;
  padding: 48px 0 56px;
}

.topbar {
  position: sticky;
  top: env(safe-area-inset-top, 0px);
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: 0 -12px 28px;
  padding: 12px;
  border: 1px solid rgba(148, 163, 184, 0.12);
  border-radius: 18px;
  background: rgba(2, 6, 23, 0.88);
  box-shadow: 0 12px 30px rgba(2, 6, 23, 0.24);
  backdrop-filter: blur(18px);
}

.topbar-status {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-left: auto;
}

.bottom-settings {
  display: grid;
  gap: 12px;
  margin-top: 28px;
}

.bottom-settings .status-pill {
  justify-self: start;
}

.bottom-settings .connection-panel {
  width: 100%;
}

.connection-panel {
  display: grid;
  grid-template-columns: auto minmax(260px, 360px);
  gap: 18px;
  flex-basis: 100%;
  align-items: end;
  padding: 14px;
  border: 1px solid rgba(56, 189, 248, 0.24);
  border-radius: 14px;
  background: rgba(15, 23, 42, 0.74);
}

.group-settings {
  display: flex;
  align-items: end;
  gap: 8px;
}

.video-send-form {
  display: grid;
  gap: 7px;
}

.playlist-form {
  grid-column: 1 / -1;
  padding-top: 14px;
  border-top: 1px solid rgba(148, 163, 184, 0.16);
}

.video-send-form label {
  color: var(--text);
  font-size: 0.78rem;
  font-weight: 700;
}

.video-send-fields {
  display: flex;
  gap: 8px;
}

.video-send-fields input {
  min-width: 0;
  width: 100%;
  padding: 9px 10px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: rgba(2, 6, 23, 0.64);
  color: var(--text);
  font: inherit;
  font-size: 0.82rem;
}

.video-send-fields input:focus {
  border-color: var(--accent);
  outline: none;
}

.video-send-fields button {
  flex: none;
  padding: 8px 12px;
  border: 1px solid rgba(56, 189, 248, 0.55);
  border-radius: 8px;
  background: rgba(56, 189, 248, 0.16);
  color: var(--accent);
  cursor: pointer;
  font: inherit;
  font-size: 0.82rem;
  font-weight: 700;
}

.video-send-fields button:hover,
.video-send-fields button:focus-visible {
  background: rgba(56, 189, 248, 0.28);
  outline: none;
}

.video-send-fields button:disabled {
  cursor: wait;
  opacity: 0.6;
}

.video-send-feedback {
  margin: 0;
  font-size: 0.76rem;
}

.video-send-feedback.is-success {
  color: var(--success);
}

.video-send-feedback.is-error {
  color: #fca5a5;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 118px;
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: rgba(15, 23, 42, 0.8);
  color: var(--text);
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  font: inherit;
}

.status-pill.is-connected {
  border-color: rgba(52, 211, 153, 0.5);
  color: var(--success);
}

.status-pill.is-connecting {
  border-color: rgba(251, 191, 36, 0.5);
  color: var(--warning);
}

.status-pill.is-disconnected {
  border-color: rgba(248, 113, 113, 0.55);
  color: #fca5a5;
}

.client-id {
  width: 110px;
  padding: 7px 10px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: rgba(15, 23, 42, 0.8);
  color: var(--muted);
  font-size: 0.82rem;
  letter-spacing: 0.03em;
}

.client-id:focus {
  border-color: var(--accent);
  outline: none;
  color: var(--text);
}

.client-id-field {
  display: none;
}

.group-id-save {
  padding: 7px 10px;
  border: 1px solid rgba(52, 211, 153, 0.5);
  border-radius: 8px;
  background: rgba(52, 211, 153, 0.14);
  color: var(--success);
  font: inherit;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
}

.group-id-save:hover {
  background: rgba(52, 211, 153, 0.24);
}

.sse-toggle {
  display: none;
  padding: 7px 10px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: rgba(15, 23, 42, 0.8);
  color: var(--muted);
  cursor: pointer;
  font-size: 0.78rem;
}

.sse-toggle:hover,
.sse-toggle:focus-visible {
  border-color: var(--accent);
  color: var(--text);
  outline: none;
}

.cache-refresh {
  width: 100%;
  margin-top: 18px;
  padding: 11px 14px;
  border: 1px solid rgba(125, 211, 252, 0.6);
  border-radius: 10px;
  background: #075985;
  color: #e0f2fe;
  cursor: pointer;
  font: inherit;
  font-size: 0.86rem;
  font-weight: 700;
}

.cache-refresh:hover,
.cache-refresh:focus-visible {
  border-color: var(--accent);
  outline: none;
}

.brand-mark {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  padding: 0;
  border: 0;
  border-radius: 12px;
  background: #fff;
  color: var(--text);
  cursor: pointer;
}

.brand-mark svg {
  width: 26px;
  height: 26px;
  color: var(--accent);
  fill: currentColor;
}

.brand-mark:hover,
.brand-mark:focus-visible {
  background: #f1f5f9;
  outline: none;
}

.menu-toggle,
.menu-close {
  border: 1px solid var(--border);
  color: var(--text);
  background: rgba(15, 23, 42, 0.82);
  cursor: pointer;
}

.menu-toggle {
  display: grid;
  gap: 5px;
  width: 48px;
  height: 48px;
  place-content: center;
  border-radius: 15px;
  box-shadow: 0 12px 30px rgba(2, 6, 23, 0.25);
}

.menu-toggle:hover,
.menu-toggle:focus-visible,
.menu-close:hover,
.menu-close:focus-visible {
  border-color: var(--accent);
  outline: none;
}

.menu-toggle span {
  display: block;
  width: 20px;
  height: 2px;
  border-radius: 2px;
  background: var(--accent);
}

.notification-bubble {
  position: fixed;
  top: 24px;
  right: 24px;
  z-index: 25;
  width: 14px;
  height: 14px;
  border: 1px solid rgba(56, 189, 248, 0.3);
  border-radius: 50%;
  background: var(--accent);
  box-shadow: 0 0 0 5px rgba(56, 189, 248, 0.18), 0 8px 24px rgba(2, 6, 23, 0.5);
  backdrop-filter: blur(18px);
}

.menu-backdrop {
  position: fixed;
  z-index: 10;
  inset: 0;
  width: 100%;
  border: 0;
  background: rgba(2, 6, 23, 0.68);
  backdrop-filter: blur(4px);
  cursor: pointer;
}

.video-menu {
  position: fixed;
  z-index: 15;
  top: 24px;
  right: 24px;
  bottom: 24px;
  width: min(420px, calc(100vw - 32px));
  padding: 24px;
  overflow-y: auto;
  border-color: rgba(56, 189, 248, 0.28);
  box-shadow: -20px 24px 90px rgba(2, 6, 23, 0.58);
}

.menu-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
}

.menu-heading h2 {
  margin: 0;
  font-size: 1.6rem;
}

.menu-close {
  width: 36px;
  height: 36px;
  border-radius: 11px;
  color: var(--muted);
  font-size: 1.6rem;
  line-height: 1;
}

.menu-count {
  margin: 24px 0 18px;
  color: var(--muted);
  font-size: 0.9rem;
}

.video-menu .video-card {
  align-items: center;
  margin-top: 10px;
}

.video-index {
  display: grid;
  place-items: center;
  flex: none;
  width: 2.75rem;
  height: 2.75rem;
  border: 2px solid var(--accent);
  border-radius: 50%;
  color: var(--accent);
  font-size: 1.35rem;
  font-weight: 800;
  letter-spacing: 0;
  line-height: 1;
}

.video-menu .video-meta {
  min-width: 0;
}

.video-menu .video-title,
.video-menu .video-url {
  display: block;
}

.video-menu .video-action {
  font-size: 0.78rem;
  white-space: nowrap;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 180ms ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.notification-enter-active,
.notification-leave-active {
  transition: opacity 180ms ease, transform 180ms ease;
}

.notification-enter-from,
.notification-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 220ms cubic-bezier(0.22, 1, 0.36, 1), opacity 220ms ease;
}

.slide-enter-from,
.slide-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

.eyebrow,
.player-kicker {
  margin: 0 0 10px;
  text-transform: uppercase;
  letter-spacing: 0.16em;
  color: var(--accent);
  font-size: 0.78rem;
  font-weight: 700;
}

h2,
p {
  margin-top: 0;
}

.layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 20px;
  align-items: start;
}

.panel {
  background: var(--bg-soft);
  border: 1px solid var(--border);
  border-radius: 24px;
  backdrop-filter: blur(18px);
  box-shadow: 0 28px 80px rgba(2, 6, 23, 0.34);
}

.panel-heading,
.player-header {
  display: flex;
  align-items: start;
  justify-content: space-between;
  gap: 16px;
}

.panel-heading h2,
.player-header h2 {
  margin-bottom: 6px;
  font-size: 1.15rem;
}

.panel-heading span {
  color: var(--muted);
  font-size: 0.95rem;
}

.video-card {
  width: 100%;
  display: grid;
  grid-template-columns: auto 120px minmax(0, 1fr) auto;
  gap: 14px;
  align-items: center;
  padding: 14px;
  margin-top: 14px;
  border: 1px solid transparent;
  border-radius: 18px;
  background: var(--surface);
  color: var(--text);
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  transition: transform 180ms ease, border-color 180ms ease, background 180ms ease;
}

.video-card:hover,
.video-card:focus-visible {
  transform: translateY(-1px);
  border-color: rgba(56, 189, 248, 0.5);
  background: var(--surface-strong);
  outline: none;
}

.video-card.active {
  border-color: rgba(56, 189, 248, 0.85);
  background: rgba(56, 189, 248, 0.16);
}

.video-thumbnail {
  position: relative;
  overflow: hidden;
  aspect-ratio: 16 / 9;
  border-radius: 14px;
  border: 1px solid var(--border);
  background: rgba(2, 6, 23, 0.4);
}

.video-thumbnail img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.video-play {
  position: absolute;
  left: 10px;
  bottom: 10px;
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  border-radius: 999px;
  background: rgba(2, 6, 23, 0.75);
  color: var(--text);
  font-size: 0.72rem;
}

.video-title {
  margin-bottom: 6px;
  font-weight: 700;
}

.video-url {
  margin: 0;
  color: var(--muted);
  font-size: 0.88rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.video-action {
  color: var(--accent);
  font-size: 0.86rem;
  font-weight: 700;
}

.player-panel {
  padding: 24px;
}

.player-header {
  margin-bottom: 20px;
}

.player-header a {
  align-self: flex-start;
  padding: 10px 14px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.16);
  color: var(--accent);
  text-decoration: none;
  font-size: 0.92rem;
  font-weight: 700;
}

.player-frame {
  overflow: hidden;
  border-radius: 22px;
  background: rgba(2, 6, 23, 0.68);
  aspect-ratio: 16 / 9;
}

.player-frame iframe {
  width: 100%;
  height: 100%;
  border: 0;
}

.video-navigation {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 4px 0 0;
  border: 0;
  border-radius: 999px;
  background: transparent;
}

.video-navigation button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 50px;
  padding: 8px;
  border: 1px solid rgba(74, 222, 128, 0.55);
  border-radius: 10px;
  background: #166534;
  color: #dcfce7;
  cursor: pointer;
  font: inherit;
  font-size: 1.55rem;
  font-weight: 700;
  line-height: 1;
}

.video-navigation button:hover:not(:disabled),
.video-navigation button:focus-visible:not(:disabled) {
  border-color: #7dd3fc;
  background: #0284c7;
  outline: none;
}

.video-navigation .previous-button {
  border-color: #f97316;
  background: #f97316;
  color: #fff;
}

.video-navigation .previous-button:hover:not(:disabled),
.video-navigation .previous-button:focus-visible:not(:disabled) {
  border-color: #c2410c;
  background: #c2410c;
}

.video-navigation .next-button {
  border-color: #f97316;
  background: #f97316;
  color: #fff;
}

.video-navigation .next-button:hover:not(:disabled),
.video-navigation .next-button:focus-visible:not(:disabled) {
  border-color: #c2410c;
  background: #c2410c;
}

.video-navigation button:disabled {
  cursor: not-allowed;
  opacity: 0.38;
}

@media (max-width: 560px) {
  .video-navigation {
    width: 100%;
    justify-content: space-between;
    gap: 8px;
  }

  .video-navigation button {
    width: 64px;
    height: 46px;
    padding-right: 8px;
    padding-left: 8px;
  }
}

.playback-controls {
  display: flex;
  gap: 10px;
  margin-top: 14px;
}

.media-controls {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
}

.media-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0 48px;
  gap: 18px;
}

.media-controls .playback-controls,
.media-controls .volume-controls {
  flex: 1 1 0;
  justify-content: space-evenly;
  margin-top: 0;
}

.playback-controls button {
  padding: 10px 16px;
  border: 1px solid var(--border);
  border-radius: 999px;
  background: var(--surface);
  color: var(--text);
  cursor: pointer;
  font: inherit;
  font-weight: 700;
}

.playback-controls button:hover,
.playback-controls button:focus-visible {
  background: var(--surface-strong);
  border-color: var(--accent);
}

.playback-controls button:first-child {
  border-color: rgba(59, 130, 246, 0.7);
  background: rgba(59, 130, 246, 0.18);
  color: #93c5fd;
}

.playback-controls button:last-child {
  border-color: rgba(248, 113, 113, 0.7);
  background: rgba(248, 113, 113, 0.18);
  color: #fca5a5;
}

.playback-controls button:first-child:hover,
.playback-controls button:first-child:focus-visible {
  border-color: #3b82f6;
  background: rgba(59, 130, 246, 0.32);
}

.playback-controls button:last-child:hover,
.playback-controls button:last-child:focus-visible {
  border-color: #ef4444;
  background: rgba(239, 68, 68, 0.32);
}

.volume-controls {
  display: flex;
  gap: 6px;
  margin-top: 0;
}

.volume-controls button {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 1px solid var(--border);
  border-radius: 999px;
  background: var(--surface);
  color: var(--text);
  cursor: pointer;
  font: inherit;
  font-size: 1.2rem;
  font-weight: 700;
}

.volume-controls button:hover,
.volume-controls button:focus-visible {
  background: var(--surface-strong);
  border-color: var(--accent);
  outline: none;
}

.volume-controls button:first-child {
  border-color: rgba(250, 204, 21, 0.65);
  background: rgba(250, 204, 21, 0.16);
  color: #fde047;
}

.volume-controls button:last-child {
  border-color: rgba(52, 211, 153, 0.65);
  background: rgba(52, 211, 153, 0.16);
  color: #6ee7b7;
}

.volume-controls button:first-child:hover,
.volume-controls button:first-child:focus-visible {
  border-color: #facc15;
  background: rgba(250, 204, 21, 0.28);
}

.volume-controls button:last-child:hover,
.volume-controls button:last-child:focus-visible {
  border-color: #34d399;
  background: rgba(52, 211, 153, 0.28);
}

.volume-bar {
  display: block;
  width: 210px;
  height: 8px;
  border-radius: 999px;
  background: linear-gradient(to right, #34d399 var(--volume-level), rgba(148, 163, 184, 0.3) var(--volume-level));
}

@media (max-width: 920px) {
  .topbar {
    flex-wrap: wrap;
  }

  .topbar-status {
    order: 3;
    width: 100%;
    margin-left: 0;
  }

  .connection-panel {
    grid-template-columns: minmax(0, 1fr);
  }

  .video-card {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .video-thumbnail {
    grid-column: 1 / -1;
  }

  .video-action {
    justify-self: end;
  }
}

@media (min-width: 1440px) {
  .app-shell {
    width: min(1600px, calc(100vw - 48px));
    padding-top: 28px;
  }

  .player-panel {
    padding: 28px;
  }

  .player-frame {
    min-height: calc(100vh - 190px);
    aspect-ratio: auto;
  }
}

@media (display-mode: standalone), (display-mode: fullscreen) {
  .app-shell {
    width: min(1600px, calc(100vw - 32px));
    padding-top: max(20px, env(safe-area-inset-top));
    padding-right: max(0px, env(safe-area-inset-right));
    padding-left: max(0px, env(safe-area-inset-left));
  }

  .player-frame {
    height: max(420px, calc(100dvh - 190px));
    min-height: 0;
    aspect-ratio: auto;
  }
}

@media (max-width: 640px) {
  .app-shell {
    width: min(100vw - 20px, 1180px);
    padding: 20px 0 36px;
  }

  .topbar {
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 18px;
  }

  .topbar-status {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
    width: 100%;
    gap: 8px;
  }

  .connection-panel {
    display: grid;
    grid-column: 1 / -1;
    padding: 12px;
  }

  .group-settings,
  .video-send-fields {
    width: 100%;
  }

  .status-pill {
    grid-column: 1 / -1;
    min-width: 0;
    width: 100%;
  }

  .client-id {
    width: 100%;
    min-width: 0;
    min-height: 42px;
    padding: 9px 10px;
  }

  .topbar-status > .client-id:not(.client-id-field) {
    grid-column: 1 / 3;
  }

  .playback-controls {
    gap: 6px;
    margin-top: 0;
  }

  .media-controls {
    grid-column: 1 / -1;
    justify-content: center;
    gap: 12px;
  }

  .media-actions {
    padding-right: 20px;
    padding-left: 20px;
    gap: 8px;
  }

  .playback-controls button {
    min-width: 44px;
    min-height: 42px;
    padding: 8px 12px;
  }

  .volume-controls {
    justify-content: flex-end;
  }

  .volume-bar {
    width: 210px;
  }

  .volume-controls button {
    width: 42px;
    height: 42px;
  }

  .sse-toggle {
    grid-column: 1 / -1;
    width: 100%;
    min-height: 42px;
  }

  .player-header {
    flex-direction: column;
    gap: 12px;
  }

  .player-header a {
    width: 100%;
    text-align: center;
  }

  .menu-toggle {
    display: none;
  }

  .menu-toggle {
    flex: none;
    width: 44px;
    height: 44px;
    border-radius: 13px;
  }

  .video-menu {
    position: static;
    width: 100%;
    max-height: none;
    margin-top: 20px;
    padding: 18px;
    border-radius: 20px;
  }

  .menu-backdrop {
    display: none;
  }

  .menu-heading h2 {
    font-size: 1.35rem;
  }

  .menu-count {
    margin: 18px 0 14px;
  }

  .video-menu .video-card {
    min-height: 58px;
    padding: 12px;
    gap: 10px;
    border-radius: 14px;
  }

  .video-menu .video-action {
    padding: 8px 4px;
  }

  .panel,
  .video-menu {
    border-radius: 20px;
  }

  .player-panel,
  .video-menu {
    padding: 18px;
  }

}

@media (max-width: 920px) {
  .menu-toggle,
  .menu-backdrop {
    display: none;
  }

  .video-menu {
    position: static;
    width: 100%;
    max-height: none;
    margin-top: 20px;
  }
}

.app-shell.is-mobile-layout .menu-toggle,
.app-shell.is-mobile-layout .menu-backdrop {
  display: none;
}

.app-shell.is-mobile-layout .video-menu {
  position: static;
  width: 100%;
  max-height: none;
  margin-top: 20px;
}
</style>
